import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { StandardProps } from "@/components/componentMap";
import {
  getPromoMessaging,
  trimMessageTerms,
  MessageWithCategoryAndType,
  categoriseMessages,
  groupMessagesByCategory,
  combinationOfferTypes,
} from "@/utils/product/getPromoMessaging";
import { getCleanedPromoDescription } from "@/utils/product/cleanPromoDescription";
import {
  getTargetProductId,
  getSecondTargetProductId,
  getGWPTotal,
} from "@/utils/product/getProductFromPromoMessaging";
import { removeIFCMessages } from "@/utils/product/interestFreeCredit/removeIFCMessages";
import { findPromoCode } from "@/utils/product/getPromoCode";
import { getBasketData } from "@/utils/product/getBasketData";
import {
  TargetProduct,
  CurrentProduct,
  SpendStretchProduct,
} from "@/utils/product/getPromoOffersFromBasket";
import {
  multipleQualifyingCombinationOffer,
  getPromptFromCombinationOffer,
} from "./utils";
import { isDiorBrand } from "@JohnLewisPartnership/jl-design-system/dist/utils/brands/brands";
import { ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED } from "@/constants/constants";
import { PromotionalPromptUI } from "./PromotionalPromptUI";
import { useVariant } from "@/hooks/useVariant/useVariant";

export const PromotionalPrompt = ({
  product,
  runtimeConfig,
}: Pick<StandardProps, "product" | "runtimeConfig">) => {
  const basketEndpoint = runtimeConfig.basketApiBaseUrl;

  const [promptMessage, setPromptMessage] =
    useState<MessageWithCategoryAndType>();
  const [termsAndConditions, setTermsAndConditions] = useState<
    string | undefined
  >("");
  const [promoCode, setPromoCode] = useState("");

  const { variant } = useVariant(product);
  const { services = [] } = variant;
  const { brand } = product;

  const guarantees = services.filter(
    ({ __typename }) => __typename === "IncludedGuarantee",
  );
  const warrantyServices = services.filter(({ __typename }) =>
    ["ExtendedWarranty"].includes(__typename),
  );

  const cleanedMessages = removeIFCMessages(product.messaging || []);
  const promoDrawerMessages = getPromoMessaging(
    cleanedMessages,
    true,
  ).promoDrawerMessages;
  const promotions = promoDrawerMessages.map((promotion) =>
    trimMessageTerms(promotion),
  );
  const messagesWithCategoryAndType = categoriseMessages(promotions);
  const { offers } = groupMessagesByCategory(messagesWithCategoryAndType);

  const hasServices = useRef(
    guarantees.length > 0 || (warrantyServices && warrantyServices?.length > 0),
  );
  const targetProducts = useRef<Array<TargetProduct>>([]);
  const currentProduct = useRef<CurrentProduct>();
  const spendStretchProduct = useRef<SpendStretchProduct>();

  const promoPromptMessages = useMemo(() => {
    const filteredMessages = offers.filter(
      (promoMessage: MessageWithCategoryAndType) =>
        combinationOfferTypes.includes(promoMessage.promotionType),
    );
    return filteredMessages;
  }, [offers]);

  const handleBasketEvent = useCallback(async () => {
    if (!hasServices.current) {
      const setPromotionCode = (
        promptDisplayMessage: MessageWithCategoryAndType,
      ) => {
        let promoCode = findPromoCode(promptDisplayMessage.title);
        if (!promoCode && promptDisplayMessage.description) {
          promoCode = findPromoCode(promptDisplayMessage.description);
        }
        setPromoCode(promoCode || "");
      };
      const getBasketDataThenSetPrompt = () => {
        getBasketData(basketEndpoint)
          .then(async (basketData) => {
            if (!basketData.error) {
              const prompt = getPromptFromCombinationOffer(
                currentProduct.current,
                spendStretchProduct.current,
                targetProducts.current,
                basketData.data,
              );
              if (prompt) {
                setPromptMessage(prompt);
                setPromotionCode(prompt);
              }
            }
          })
          .catch();
      };
      promoPromptMessages.map((promotion) => {
        const { description, terms } = getCleanedPromoDescription(
          true,
          promotion,
        );
        const promoDescription = description || "";
        setTermsAndConditions(terms);

        const multipleQuantity =
          multipleQualifyingCombinationOffer(promoDescription);

        if (multipleQuantity) {
          currentProduct.current = {
            productId: product.aliases.pimProductId,
            promoMessage: promotion,
            qualifyingProductQuantity: multipleQuantity,
          };
        } else {
          const spendStretchTotal = getGWPTotal(promotion.title);
          const isSpendStretch = spendStretchTotal ? true : false;

          const targetProductId = getTargetProductId(description);
          const secondTargetProductId = getSecondTargetProductId(description);
          const combinationProductId =
            targetProductId === product.aliases.pimProductId &&
            secondTargetProductId
              ? secondTargetProductId
              : targetProductId;

          if (isSpendStretch) {
            spendStretchProduct.current = {
              promoMessage: promotion,
              targetProductId: combinationProductId || undefined,
              spendStretchTotal: spendStretchTotal,
            };
          } else if (combinationProductId) {
            targetProducts.current.push({
              productId: combinationProductId,
              promoMessage: promotion,
            });
          }
        }
      });
      getBasketDataThenSetPrompt();
    }
  }, [product.aliases.pimProductId, promoPromptMessages, basketEndpoint]);

  useEffect(() => {
    window.addEventListener(
      ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED,
      handleBasketEvent,
    );

    return () => {
      window.removeEventListener(
        ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED,
        handleBasketEvent,
      );
    };
  }, [handleBasketEvent]);

  const shouldNotRender = () => {
    return !promptMessage || hasServices.current;
  };

  if (shouldNotRender()) return null;

  return (
    promptMessage && (
      <PromotionalPromptUI
        message={promptMessage.clippedTitle}
        promoCode={promoCode}
        isBlackOverride={isDiorBrand(brand?.name)}
        terms={termsAndConditions}
      />
    )
  );
};
